import { ToastNotifications } from '@lightspeed/design-system-react'
import { useT } from '@transifex/react'
import {
  FulfilmentDetail,
  FulfilmentType,
} from '../../../../../../models/fulfilment'
import { useAppSelector } from '../../../../../../state/hooks'
import { selectIsSaleCustomerContactInfoReviewed } from '../../../../../../state/slices/uiStateSlice'
import {
  CurrentView,
  ShouldFulfillOpt,
  useLineItemFulfillmentContext,
} from '../LineItemFulfillmentContext'
import { isMissingContactInfo } from '../LineItemFulfillmentUtils'

interface UseLineItemFulfillmentDecisionFlowProps {
  onFulfilmentDetailsSet: (fulfilmentDetails: FulfilmentDetail) => void
  onClose: () => void
  onBreakdownOuterPacks?: () => Promise<boolean>
  saleHasServiceOrder?: boolean
}

export const useLineItemFulfillmentDecisionFlow = ({
  onFulfilmentDetailsSet,
  onClose,
  onBreakdownOuterPacks,
  saleHasServiceOrder = false,
}: UseLineItemFulfillmentDecisionFlowProps) => {
  const t = useT()
  const {
    setCurrentView,
    currentDecision,
    currentCustomer,
    onSelectContinueAnyway,
    setCurrentDecision,
    isBreakdownSelected,
    setIsBreakdownSelected,
  } = useLineItemFulfillmentContext()

  const isContactInfoReviewed = useAppSelector(
    selectIsSaleCustomerContactInfoReviewed
  )

  const handleContinueFromDecisionView = async () => {
    if (currentDecision === ShouldFulfillOpt.YES) {
      // Check customer requirements first (same for both service and non-service sales)
      if (!currentCustomer) {
        setCurrentView(CurrentView.ADD_CUSTOMER)
        return
      }

      if (isMissingContactInfo(currentCustomer) || !isContactInfoReviewed) {
        setCurrentView(CurrentView.REVIEW_CONTACT_INFO)
        return
      }

      // Customer info is complete - proceed based on sale type
      if (saleHasServiceOrder) {
        // For service sales, skip fulfillment method view
        // Clear any NO_INVENTORY error since JAWS handles fulfillment
        if (onSelectContinueAnyway) {
          onSelectContinueAnyway()
        }
        // Set to PICKUP so item appears in "TO BE ORDERED FOR SERVICE" section
        onFulfilmentDetailsSet({
          type: FulfilmentType.PICKUP,
          note: '',
        })
        onClose()
      } else {
        setCurrentView(CurrentView.FULFILMENT_METHOD)
      }
    } else {
      // Handle both "Continue anyway" and "Break down" cases.
      if (isBreakdownSelected) {
        const isBreakdownSuccessful = await onBreakdownOuterPacks?.()
        if (!isBreakdownSuccessful) {
          ToastNotifications.negative(
            t('Sorry, we could not break down the product.', {
              _context: 'Toast Notification',
            })
          )
        }
      }

      if (onSelectContinueAnyway) {
        onSelectContinueAnyway()
      }
      onFulfilmentDetailsSet({
        type: FulfilmentType.UNSPECIFIED,
        note: '',
      })
      onClose()
    }
  }

  const handleDecisionSelect = (
    decision: ShouldFulfillOpt,
    isBreakdown: boolean = false
  ) => {
    setCurrentDecision(decision)
    setIsBreakdownSelected(isBreakdown)
  }

  return {
    handleDecisionSelect,
    handleContinueFromDecisionView,
  }
}
